import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NavParams, ViewController } from "ionic-angular";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import AddressType from "../../../models/address-type";
import Address from "../../../models/contact/address";
import SortOptions from "../../../models/content-wrapper/sort-options";
import CompanyResourceSummary from "../../../models/resource-management/company-resource-summary";
import CompanyResourceType from "../../../models/resource-management/company-resource-type.enum";
import { commonVar } from "../../../providers/common-var/common";
import { FacilitiesService } from "../../../providers/facilities-service/facilities.service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";

@Component({
  selector: "fax-modal",
  templateUrl: "fax-modal.html",
})
export class FaxModalComponent implements OnInit {
  selectedTab: string = "manual";
  faxForm: FormGroup;
  providers: CompanyResourceSummary[] = [];
  pharmacies: any[] = [];
  funeralHomes: any[] = [];
  facilities: any[] = [];
  faxNumbers: any[] = [];
  providersTypeahead = new Subject<string>();
  entityTypeahead = new Subject<string>();
  subjectCharCount: number = 0;
  maxSubjectLength: number = 255;
  phoneMask = commonVar.PHONE_US_MASK;
  searchTerm: string = "";
  showNoFaxError: boolean = false;
  noFaxErrorMessage: string = "";
  patChartId?: number;
  patientFacilities: any = null; // Cache for patient facilities
  isLoadingPatientFacilities: boolean = false; // Flag to prevent concurrent API calls
  isSending: boolean = false; // Guard against double-submit duplicate fax POSTs (NOVA-2028)

  constructor(
    private viewCtrl: ViewController,
    private navParams: NavParams,
    private formBuilder: FormBuilder,
    private facilitiesService: FacilitiesService,
    private resourceManagementService: ResourceManagementService
  ) {}

  ngOnInit() {
    // Get patChartId from navParams
    this.patChartId = this.navParams.get("patChartId");

    // Check if we have previous form data to restore
    const formData = this.navParams.get("formData");
    if (formData) {
      this.selectedTab = formData.selectedTab || "manual";
      this.providers = formData.providers || [];
      this.pharmacies = formData.pharmacies || [];
      this.funeralHomes = formData.funeralHomes || [];
      this.facilities = formData.facilities || [];
      this.faxNumbers = formData.faxNumbers || [];
    }

    this.initializeForm();
    this.setupProvidersTypeahead();
    this.setupEntityTypeahead();
    this.setupSubjectTracking();

    // Restore form values if available
    if (formData && formData.formValues) {
      this.faxForm.patchValue(formData.formValues);
      // Restore fax numbers for selected entity
      if (formData.formValues.selectedEntity) {
        this.onEntityChange(formData.formValues.selectedEntity);
      }
    }

    // Set initial validators for the current tab
    this.setValidatorsForTab(this.selectedTab);

    // Load tab data if not manual and we have a selected tab
    if (this.selectedTab !== "manual" && !formData) {
      this.loadTabData(this.selectedTab);
    }
  }

  initializeForm() {
    this.faxForm = this.formBuilder.group({
      recipientName: [""], // Only for manual tab
      selectedEntity: [null],
      faxNumber: ["", [Validators.required, this.phoneValidator()]],
      subject: ["", this.maxCharactersWithoutSpaces(255)],
    });
  }

  // Custom validator for phone number with mask
  phoneValidator() {
    return (control: any) => {
      if (!control.value) return null;

      // Remove non-numeric characters to check if we have 10 digits
      const digitsOnly = control.value.replace(/\D/g, "");

      // Check if we have exactly 10 digits
      if (digitsOnly.length === 10) {
        return null; // Valid
      }

      return { invalidPhone: true };
    };
  }

  setValidatorsForTab(tab: string) {
    if (tab !== "manual") {
      this.faxForm.get("selectedEntity").setValidators([Validators.required]);
      this.faxForm.get("recipientName").clearValidators();
    } else {
      this.faxForm.get("selectedEntity").clearValidators();
      this.faxForm.get("recipientName").setValidators([Validators.required]);
    }
    this.faxForm.get("selectedEntity").updateValueAndValidity();
    this.faxForm.get("recipientName").updateValueAndValidity();
  }

  // Custom validator to limit characters without counting spaces
  maxCharactersWithoutSpaces(max: number) {
    return (control: any) => {
      if (!control.value) return null;

      const valueWithoutSpaces = control.value.replace(/\s/g, "");
      if (valueWithoutSpaces.length > max) {
        return {
          maxCharactersWithoutSpaces: {
            max,
            actual: valueWithoutSpaces.length,
          },
        };
      }
      return null;
    };
  }

  setupSubjectTracking() {
    // Track character count for subject field (excluding spaces)
    this.faxForm.get("subject").valueChanges.subscribe((value: string) => {
      if (value) {
        // Count characters excluding spaces
        this.subjectCharCount = value.replace(/\s/g, "").length;
      } else {
        this.subjectCharCount = 0;
      }
    });
  }

  setupProvidersTypeahead() {
    this.providersTypeahead
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          if (term && term.length > 2) {
            return this.resourceManagementService.getWorkforceNonVolunteerFilter(
              0,
              10000,
              term,
              new SortOptions("last_name", "asc"),
              CompanyResourceType.PROVIDER,
              "", // activityLog
              true, // nonCredential
              false, // nonSystemUser
              "", // location auto set current user TenantId
              null, // professionalRelation
              0, // companyId
              true, // showInactiveUsers
              null
            );
          } else {
            this.providers = [];
            return [];
          }
        })
      )
      .subscribe(
        (response) => {
          // Transform to unified structure
          this.providers = (response?.content || []).map((item: any) => ({
            ...item,
            displayName: this.addAddressToDisplayName(item),
            faxNumbers: item.faxNumber
              ? [this.formatFaxNumber(item.faxNumber)]
              : [],
          }));
        },
        (error) => {
          console.error("Error searching providers:", error);
          this.providers = [];
        }
      );
  }

  addAddressToDisplayName(resourceSummary: CompanyResourceSummary): string {
    const displayName = resourceSummary.displayName || "";

    if (!resourceSummary.addresses || resourceSummary.addresses.length === 0) {
      return displayName;
    }

    const addressTypePriority = [
      AddressType.OFFICE,
      AddressType.PHYSICAL,
      AddressType.HOME,
      AddressType.MAILING,
    ];

    let bestAddress: Address | undefined;

    for (const type of addressTypePriority) {
      bestAddress = resourceSummary.addresses.find(
        (addr) => addr.addressType === type
      );
      if (bestAddress) {
        break;
      }
    }

    if (bestAddress) {
      const addressParts = [
        bestAddress.address1,
        bestAddress.city,
        bestAddress.state,
      ].filter((part) => part); // The filter removes null, undefined, or empty strings.

      if (addressParts.length > 0) {
        return `${displayName} | ${addressParts.join(", ")}`;
      }
    }

    return displayName;
  }

  /**
   * Format display name with address for Pharmacy/Funeral Home entities
   * @param item - Entity with name and address object
   * @returns Formatted string: "Name | Address1, City, State"
   */
  addAddressToPharmacyOrFuneralHome(item: any): string {
    const displayName = item.name || "";

    if (!item.address) {
      return displayName;
    }

    const addressParts = [
      item.address.address1,
      item.address.city,
      item.address.state,
    ].filter((part) => part); // Filter removes null, undefined, or empty strings

    if (addressParts.length > 0) {
      return `${displayName} | ${addressParts.join(", ")}`;
    }

    return displayName;
  }

  /**
   * Format display name with address for Facility entities
   * @param facility - Facility object with streetAddress
   * @returns Formatted string: "Name | Address1, City, State"
   */
  addAddressToFacility(facility: any): string {
    const displayName = facility.facilityName || facility.name || "";

    if (!facility.streetAddress) {
      return displayName;
    }

    const addressParts = [
      facility.streetAddress.address1,
      facility.streetAddress.city,
      facility.streetAddress.state,
    ].filter((part) => part); // Filter removes null, undefined, or empty strings

    if (addressParts.length > 0) {
      return `${displayName} | ${addressParts.join(", ")}`;
    }

    return displayName;
  }

  setupEntityTypeahead() {
    this.entityTypeahead
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term: string) => {
        this.searchTerm = term;
      });
  }

  loadTabData(tab: string) {
    switch (tab) {
      case "pharmacies":
        this.loadPharmacies();
        break;
      case "funeralHomes":
        this.loadFuneralHomes();
        break;
      case "providers":
        this.loadProviders();
        break;
      case "facilities":
        this.loadFacilities();
        break;
    }
  }

  loadPharmacies() {
    this.loadFacilitiesByType(
      "Pharmacy",
      "pharmacies",
      "PHARMACY",
      "hasPharmacy"
    );
  }

  loadFuneralHomes() {
    this.loadFacilitiesByType(
      "FuneralHome",
      "funeralHomes",
      "FUNERALHOME",
      "hasFuneralHome"
    );
  }

  /**
   * Generic method to load facilities by type and handle preselection
   * @param searchType - The facility type to search for (e.g., "Pharmacy", "FuneralHome")
   * @param arrayProperty - The property name to store results (e.g., "pharmacies", "funeralHomes")
   * @param facilityType - The facility type constant used in patient facilities (e.g., "PHARMACY", "FUNERALHOME")
   * @param hasFlag - The flag property to check in patient facilities (e.g., "hasPharmacy", "hasFuneralHome")
   */

  private loadFacilitiesByType(
    searchType: string,
    arrayProperty: "pharmacies" | "funeralHomes",
    facilityType: string,
    hasFlag: string
  ) {
    this.facilitiesService
      .searchFacilitiesByType("", searchType, 0, 9999)
      .subscribe(
        (response) => {
          // Transform to unified structure
          this[arrayProperty] = (response.content || []).map((item: any) => ({
            ...item,
            displayName: this.addAddressToPharmacyOrFuneralHome(item),
            faxNumbers:
              item.address && item.address.fax
                ? [this.formatFaxNumber(item.address.fax)]
                : [],
          }));

          // Load patient facilities and preselect
          this.loadPatientFacilitiesAndPreselect(
            arrayProperty,
            facilityType,
            hasFlag
          );
        },
        (error) => {
          console.error(`Error loading ${searchType.toLowerCase()}s:`, error);
          this[arrayProperty] = [];
        }
      );
  }

  /**
   * Load patient facilities from API if needed and trigger preselection
   */
  private loadPatientFacilitiesAndPreselect(
    arrayProperty: "pharmacies" | "funeralHomes",
    facilityType: string,
    hasFlag: string
  ) {
    if (
      this.patChartId &&
      this.patientFacilities === null &&
      !this.isLoadingPatientFacilities
    ) {
      // First time loading patient facilities
      this.isLoadingPatientFacilities = true;
      this.facilitiesService
        .getPatientChartFacilities(this.patChartId)
        .subscribe(
          (patientFacilitiesResponse) => {
            this.patientFacilities = patientFacilitiesResponse;
            this.isLoadingPatientFacilities = false;
            this.preselectFacility(arrayProperty, facilityType, hasFlag);
          },
          (error) => {
            console.error("Error loading patient facilities:", error);
            this.patientFacilities = {}; // Set to empty to prevent retry
            this.isLoadingPatientFacilities = false;
          }
        );
    } else if (
      this.patientFacilities &&
      Object.keys(this.patientFacilities).length > 0
    ) {
      // Already cached, just preselect
      this.preselectFacility(arrayProperty, facilityType, hasFlag);
    }
  }

  /**
   * Generic preselection method for facilities
   * @param arrayProperty - The property name containing loaded facilities (e.g., "pharmacies", "funeralHomes")
   * @param facilityType - The facility type to find in patient facilities (e.g., "PHARMACY", "FUNERALHOME")
   * @param hasFlag - The flag property to check (e.g., "hasPharmacy", "hasFuneralHome")
   */
  private preselectFacility(
    arrayProperty: "pharmacies" | "funeralHomes",
    facilityType: string,
    hasFlag: string
  ) {
    if (
      this.patientFacilities?.[hasFlag] &&
      this.patientFacilities.facilities
    ) {
      const patientFacility = this.patientFacilities.facilities.find(
        (f: any) => f.facilityType === facilityType
      );
      if (patientFacility) {
        const matchedFacility = this[arrayProperty].find(
          (item) => item.id === patientFacility.id
        );
        if (matchedFacility) {
          // Use setTimeout to ensure ng-select options are rendered before setting value
          setTimeout(() => {
            this.faxForm.patchValue({ selectedEntity: matchedFacility });
            this.onEntityChange(matchedFacility);
          }, 0);
        }
      }
    }
  }

  loadProviders() {
    // Don't load providers by default - they will be loaded when user types in search via typeahead
    this.providers = [];
  }

  loadFacilities() {
    // Create search request body for facilities
    const searchDetails = {
      DataType: "FACILITY",
      mustFields: [
        {
          name: "dataType",
          value: "FACILITY",
        },
        {
          name: "isActive",
          value: "true",
        },
      ],
      SearchString: null,
      dateRange: null,
      OptionalFields: [],
      sortBy: {
        name: "facilityName",
        value: "asc",
      },
      From: 0,
      Size: 9999,
    };

    this.facilitiesService.getFacilityFromOpenSearch(searchDetails).subscribe(
      (response) => {
        if (response && response["hits"] && response["hits"].length > 0) {
          // Transform to unified structure
          this.facilities = response["hits"].map((facility: any) => {
            // Extract all fax numbers from facility contacts
            const faxNumbers = [];
            if (
              facility.facilityContacts &&
              facility.facilityContacts.length > 0
            ) {
              facility.facilityContacts.forEach((contact: any) => {
                if (contact.faxNumber && contact.faxNumber.trim()) {
                  faxNumbers.push(this.formatFaxNumber(contact.faxNumber));
                }
              });
            }

            // SUP-20019: fall back to the facility-level "HIPAA/Patient Data
            // Fax #" (hipaaPatientFax) when no contact carries a fax, so a
            // facility with only a facility-level fax is still selectable.
            if (
              faxNumbers.length === 0 &&
              facility.hipaaPatientFax &&
              facility.hipaaPatientFax.trim()
            ) {
              faxNumbers.push(this.formatFaxNumber(facility.hipaaPatientFax));
            }

            return {
              ...facility,
              displayName: this.addAddressToFacility(facility),
              faxNumbers: faxNumbers,
            };
          });
        } else {
          this.facilities = [];
        }
      },
      (error) => {
        console.error("Error loading facilities:", error);
        this.facilities = [];
      }
    );
  }

  get currentEntities(): any[] {
    if (this.selectedTab === "providers") {
      return this.providers || []; // Providers are handled by API typeahead
    }

    let entities: any[] = [];
    switch (this.selectedTab) {
      case "pharmacies":
        entities = this.pharmacies || [];
        break;
      case "funeralHomes":
        entities = this.funeralHomes || [];
        break;
      case "facilities":
        entities = this.facilities || [];
        break;
      default:
        return [];
    }

    // For non-provider tabs, filter based on search term
    if (this.searchTerm && this.searchTerm.length >= 3) {
      const searchLower = this.searchTerm.toLowerCase();
      return entities.filter(
        (entity) =>
          entity.displayName &&
          entity.displayName.toLowerCase().includes(searchLower)
      );
    }

    // Show all entities by default (needed for preselection to work)
    return entities;
  }

  get entityLabel(): string {
    switch (this.selectedTab) {
      case "providers":
        return "Provider";
      case "pharmacies":
        return "Pharmacy";
      case "funeralHomes":
        return "Funeral Home";
      case "facilities":
        return "Facilities";
      default:
        return "";
    }
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
    this.searchTerm = ""; // Clear search term when switching tabs
    this.faxNumbers = []; // Clear fax numbers array when switching tabs
    this.showNoFaxError = false; // Reset error state when switching tabs
    this.noFaxErrorMessage = ""; // Reset error message when switching tabs

    // Clear providers when switching away from providers tab
    if (tab !== "providers") {
      this.providers = [];
    }

    // Reset form completely - including subject
    this.faxForm.reset({
      subject: "", // Clear subject too
      recipientName: "",
      faxNumber: "",
      selectedEntity: null,
    });

    this.setValidatorsForTab(tab);

    if (tab !== "manual") {
      this.loadTabData(tab);
    }

    // Reset the typeahead subjects to clear their state
    this.entityTypeahead.next("");
    if (tab === "providers") {
      this.providersTypeahead.next("");
    }

    // Reset subject character count
    this.subjectCharCount = 0;
  }

  onEntityChange(entity: any) {
    // Simple unified approach - entity should already have faxNumbers array populated
    if (entity && entity.faxNumbers && entity.faxNumbers.length > 0) {
      this.faxNumbers = entity.faxNumbers;
      // Set the first fax number as default
      this.faxForm.patchValue({ faxNumber: entity.faxNumbers[0] });
      this.showNoFaxError = false;
      this.noFaxErrorMessage = "";
    } else {
      this.faxNumbers = [];
      this.faxForm.patchValue({ faxNumber: "" });
      // Show error message only if an entity is actually selected (not null/undefined/empty)
      // Check for truthy entity with actual properties to avoid showing error when cleared
      if (entity && (entity.displayName || entity.name || entity.id)) {
        this.showNoFaxError = true;
        this.noFaxErrorMessage = this.getNoFaxErrorMessage();
      } else {
        this.showNoFaxError = false;
        this.noFaxErrorMessage = "";
      }
    }
  }

  onFaxInput(event: any) {
    const value = event.target.value;
    // Update the form control value manually since brmasker doesn't emit properly
    this.faxForm.get("faxNumber")?.setValue(value, { emitEvent: true });
    // Mark as touched to trigger validation
    this.faxForm.get("faxNumber")?.markAsTouched();
    // Manually trigger change detection and validation
    this.faxForm.get("faxNumber")?.updateValueAndValidity();
  }

  formatFaxNumber(fax: string): string {
    // Remove all non-numeric characters
    const cleaned = fax.replace(/\D/g, "");

    // Format as (XXX)XXX-XXXX to match phone mask pattern
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)})${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }

    // Return as-is if not a standard 10-digit number
    return fax;
  }

  cancel() {
    this.viewCtrl.dismiss({ cancelled: true });
  }

  sendFax() {
    // Double-submit guard: the Ionic loading overlay animates in asynchronously,
    // leaving a window where the button is still enabled after the first click. A
    // second click here is a no-op so we never fire duplicate fax POSTs (NOVA-2028).
    if (this.isSending) {
      return;
    }

    if (this.faxForm.valid) {
      // Create the fax data structure as required by the service
      const formValues = this.faxForm.value;

      // Determine recipient name based on tab
      let recipientName = "";
      if (this.selectedTab === "manual") {
        recipientName = formValues.recipientName;
      } else {
        // For other tabs, use the selected entity's display name
        recipientName = formValues.selectedEntity?.displayName || "";
      }

      // Clean fax number - remove all non-numeric characters
      const cleanedFaxNumber = formValues.faxNumber.replace(/\D/g, "");

      // Map selectedTab to recipientType
      let recipientType = "";
      switch (this.selectedTab) {
        case "manual":
          recipientType = "Manual";
          break;
        case "providers":
          recipientType = "Provider";
          break;
        case "pharmacies":
          recipientType = "Pharmacy";
          break;
        case "funeralHomes":
          recipientType = "Funeral Home";
          break;
        case "facilities":
          recipientType = "Facilitiy";
          break;
        default:
          recipientType = "Manual";
      }

      const faxData = {
        subject: formValues.subject || "",
        recipientType: recipientType,
        recipients: [
          {
            recipientName: recipientName,
            recipientFaxNumber: cleanedFaxNumber,
          },
        ],
        // Include form state for potential re-use if error occurs
        formState: {
          selectedTab: this.selectedTab,
          formValues: this.faxForm.value,
          providers: this.providers,
          pharmacies: this.pharmacies,
          funeralHomes: this.funeralHomes,
          facilities: this.facilities,
          faxNumbers: this.faxNumbers,
        },
      };

      // Get the callback from navParams
      const sendFaxCallback = this.navParams.get("sendFaxCallback");

      if (sendFaxCallback) {
        // Latch the guard synchronously BEFORE the async send so any second click
        // that slips through the disabled window is rejected by the check above.
        this.isSending = true;
        // Call the parent's API function and wait for response
        sendFaxCallback(faxData)
          .then((shouldClose: boolean) => {
            if (shouldClose) {
              // Only dismiss if the API call was successful
              this.viewCtrl.dismiss({ success: true });
            } else {
              // Send failed (or was blocked): modal stays open, so re-enable the
              // button and let the user try again.
              this.isSending = false;
            }
          })
          .catch(() => {
            // Defensive: if the callback ever rejects, keep the modal usable.
            this.isSending = false;
          });
      } else {
        // Fallback if no callback provided (shouldn't happen in normal flow)
        this.viewCtrl.dismiss(faxData);
      }
    }
  }

  getNoFaxErrorMessage(): string {
    switch (this.selectedTab) {
      case "pharmacies":
        return "This pharmacy does not have a Fax Number on file. Please add a fax number in Patient Chart.";
      case "funeralHomes":
        return "This funeral home does not have a Fax Number on file. Please add a fax number in Patient Chart.";
      case "facilities":
        return "This facility does not have a Fax Number on file. Please add a fax number in the Facility module.";
      case "providers":
        return "This provider does not have a Fax Number on file. Please add a fax number in Patient Chart or Workforce.";
      default:
        return "";
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/fax/fax-modal/fax-modal.ts